import { useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { supabase } from '@/integrations/supabase/client';
import { checkIsAdmin } from '@/data/producers';

type GuardState = 'loading' | 'anon' | 'forbidden' | 'ok';

/**
 * AdminRouteGuard
 * - Waits for session restore before redirecting (fixes refresh race conditions)
 * - Requires authenticated session + admin access
 */
export function AdminRouteGuard() {
  const [state, setState] = useState<GuardState>('loading');

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      // IMPORTANT: During 'loading' we must not redirect.
      const { data: { session }, error } = await supabase.auth.getSession();

      if (cancelled) return;

      if (error) {
        console.error('AdminRouteGuard: failed to get session', error);
        setState('anon');
        return;
      }

      if (!session) {
        setState('anon');
        return;
      }

      const isAdmin = await checkIsAdmin();

      if (cancelled) return;

      if (isAdmin) {
        setState('ok');
        return;
      }

      // Optional, but keeps non-admins from staying authenticated in the admin area.
      await supabase.auth.signOut();
      if (!cancelled) setState('forbidden');
    };

    run();

    return () => {
      cancelled = true;
    };
  }, []);

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (state === 'anon') {
    return <Navigate to="/admin/login" replace />;
  }

  if (state === 'forbidden') {
    return <Navigate to="/admin/login?error=not_admin" replace />;
  }

  return <Outlet />;
}
